import { useEffect, useState } from "react";
import { Loader2, Sparkles, Check, Calendar, Repeat, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { fmtMoney, errMsg } from "@/lib/format";
import { toast } from "sonner";

/**
 * Subscriptions page — Rocket Money style.
 * Lists detected recurring charges (last 120 days) that aren't tracked yet
 * and lets the user convert any of them into tracked recurring payments.
 */
export default function SubscriptionsPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ suggestions: [], total: 0 });
  const [converting, setConverting] = useState({});
  const [converted, setConverted] = useState({});

  const load = () => {
    setLoading(true);
    api.get("/transactions/detect-subscriptions")
      .then(r => setData(r.data || { suggestions: [], total: 0 }))
      .catch(e => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const convert = async (s, key) => {
    setConverting(c => ({ ...c, [key]: true }));
    try {
      await api.post("/transactions/convert-to-recurring", {
        merchant: s.merchant,
        amount: s.amount,
        currency: s.currency,
        estimated_day_of_month: s.estimated_day_of_month,
        category: s.category,
      });
      toast.success(`${s.merchant} ahora está siendo trackeado`);
      setConverted(c => ({ ...c, [key]: true }));
    } catch (e) {
      toast.error(errMsg(e, "No se pudo convertir"));
    } finally {
      setConverting(c => ({ ...c, [key]: false }));
    }
  };

  const totalsByCur = {};
  data.suggestions.forEach(s => {
    const c = (s.currency || "USD").toUpperCase();
    totalsByCur[c] = (totalsByCur[c] || 0) + Number(s.amount || 0);
  });

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-violet-400" /></div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-5 sm:p-6 space-y-6" data-testid="subscriptions-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300 font-bold">Detección automática</div>
        <h1 className="mt-1 text-2xl sm:text-3xl font-bold">Tus suscripciones recurrentes</h1>
        <p className="text-sm text-slate-400 mt-1">
          Analizamos los últimos 120 días para encontrar cargos que se repiten cada mes.
        </p>
      </header>

      {/* Summary */}
      {data.suggestions.length > 0 && (
        <div className="grid sm:grid-cols-3 gap-3" data-testid="subscriptions-summary">
          <SummaryCard label="Detectadas" value={data.total} icon={Sparkles} accent="amber" />
          {Object.entries(totalsByCur).slice(0, 2).map(([cur, total]) => (
            <SummaryCard
              key={cur}
              label={`Total estimado/mes (${cur})`}
              value={fmtMoney(total)}
              icon={Repeat}
              accent="violet"
            />
          ))}
        </div>
      )}

      {data.suggestions.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-white/15 p-16 text-center" data-testid="subscriptions-empty">
          <Sparkles className="w-12 h-12 text-violet-400 mx-auto mb-3 opacity-60" />
          <h3 className="text-lg font-bold">No detectamos suscripciones sin trackear</h3>
          <p className="mt-1 text-sm text-slate-400 max-w-md mx-auto">
            Cuando aparezcan cargos mensuales recurrentes en tus transacciones, los mostraremos aquí
            para que decidas si mantenerlos o cancelarlos.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.suggestions.map((s, i) => {
            const key = `${s.merchant}-${i}`;
            const isConv = converted[key];
            return (
              <div
                key={key}
                className={`p-4 sm:p-5 rounded-2xl border transition-colors ${
                  isConv
                    ? "bg-emerald-500/10 border-emerald-500/30"
                    : "bg-white/[0.03] border-white/10 hover:border-white/20"
                }`}
                data-testid={`subscription-card-${i}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                    isConv ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/15 text-amber-300"
                  }`}>
                    {isConv ? <Check className="w-5 h-5" /> : <Repeat className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-white">{s.merchant}</div>
                      {s.category && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-slate-400 font-bold">
                          {s.category}
                        </span>
                      )}
                    </div>
                    <div className="mt-1.5 flex items-center gap-3 text-[12px] text-slate-400 flex-wrap">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />día {s.estimated_day_of_month} del mes</span>
                      <span>· {s.occurrences} cobros detectados</span>
                      <span>· cada {Math.round(s.avg_gap_days)} días</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-lg font-bold tabular-nums">
                      {s.currency} {fmtMoney(s.amount)}
                    </div>
                    <div className="text-[11px] text-slate-500">/ mes estimado</div>
                  </div>
                </div>

                {!isConv && (
                  <div className="mt-4 flex gap-2 justify-end">
                    <button
                      onClick={() => convert(s, key)}
                      disabled={converting[key]}
                      data-testid={`subscription-track-btn-${i}`}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-sm font-semibold disabled:opacity-60 hover:opacity-90"
                    >
                      {converting[key] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      Trackear como recurrente
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 flex gap-3 items-start">
        <AlertCircle className="w-4 h-4 text-violet-400 mt-0.5 shrink-0" />
        <p className="text-[12px] text-slate-400 leading-relaxed">
          La detección busca cargos del mismo comercio con monto similar repetidos cada ~30 días.
          Si ves algo que ya cancelaste, no necesitas hacer nada — solo no lo trackees.
        </p>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, accent }) {
  const accents = {
    amber: "from-amber-500/15 to-amber-500/0 border-amber-500/25 text-amber-300",
    violet: "from-violet-500/15 to-violet-500/0 border-violet-500/25 text-violet-300",
  };
  return (
    <div className={`p-4 rounded-2xl bg-gradient-to-br ${accents[accent]} border`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-white/70 font-bold">{label}</div>
        <Icon className="w-3.5 h-3.5 opacity-70" />
      </div>
      <div className="mt-2 text-xl font-bold tabular-nums text-white">{value}</div>
    </div>
  );
}
